import {
  BaseApiRecord,
  ApiRecordKey,
  BaseApiByUserRecord,
  ApiRecordType,
  PagedContent,
} from "types/api.types";
import { SieveSearchRequestQuery } from "types/sieve.types";
import apiInstance from "utils/axios";
import { getFilters, getSorts } from "utils/sieve.utils";

export const API_PAGE_LIMIT = 50;
export const API_MAX_PAGE_LIMIT = 2147483647;

export abstract class ApiClass<TType extends ApiRecordType> {
  public abstract relativeUrl: string;
  public abstract queryKeyName: string;

  public abstract getAsync(id: ApiRecordKey): Promise<TType>;
  public abstract getAllAsync(
    filter?: string,
    sortOrder?: string,
    page?: number,
    perPage?: number
  ): Promise<PagedContent<TType>>;
  public abstract putAsync(model: ApiRecordType): Promise<TType>;
  public abstract postAsync(model: any): Promise<TType>;
  public abstract deleteAsync(id: ApiRecordKey): Promise<boolean>;
}

export abstract class BaseApi<TType extends BaseApiRecord> extends ApiClass<TType> {
  public abstract relativeUrl: string;
  public abstract queryKeyName: string;

  public async getAsync(id: ApiRecordKey): Promise<TType> {
    return apiInstance.get(`/api/${this.relativeUrl}/${id}`);
  }

  public async getAllAsync(
    filters?: string,
    sortOrder?: string,
    page?: number,
    perPage?: number
  ): Promise<PagedContent<TType>> {
    return apiInstance.get(`/api/${this.relativeUrl}/`, {
      params: {
        perPage: perPage ?? API_MAX_PAGE_LIMIT,
        filters,
        sortOrder,
      },
    });
  }

  public async getAllStandardSearchAsync(
    query?: SieveSearchRequestQuery,
    page: number = 1,
    perPage: number = API_PAGE_LIMIT,
  ): Promise<PagedContent<TType>> {
    return apiInstance.get(`/api/${this.relativeUrl}`, {
      params: { page, perPage, filters: getFilters(query), sortOrder: getSorts(query) },
    });
  }

  public putAsync = async (model: BaseApiRecord): Promise<TType> =>
    apiInstance.put(`/api/${this.relativeUrl}/${model.id}`, model);

  public postAsync = async (model: any): Promise<TType> =>
    apiInstance.post(`/api/${this.relativeUrl}`, model);

  public deleteAsync = async (id: ApiRecordKey): Promise<boolean> =>
    apiInstance.delete(`/api/${this.relativeUrl}/${id}`);
}

export abstract class BaseApiByUser<TType extends BaseApiByUserRecord> extends ApiClass<TType> {
  public abstract relativeUrl: string;
  public abstract queryKeyName: string;

  public async getAsync(userId: ApiRecordKey): Promise<TType> {
    return apiInstance.get(`/api/users/${userId}/${this.relativeUrl}`);
  }

  public async getAllAsync(): Promise<PagedContent<TType>> {
    return apiInstance.get(`/api/users/${this.relativeUrl}`, {
      params: { page: 1, perPage: API_MAX_PAGE_LIMIT },
    });
  }

  public putAsync = async (model: BaseApiByUserRecord): Promise<TType> =>
    apiInstance.put(`/api/users/${model.userId}/${this.relativeUrl}`, model);

  public postAsync = async (model: BaseApiByUserRecord): Promise<TType> =>
    apiInstance.post(`/api/users/${model.userId}/${this.relativeUrl}`, model);

  public deleteAsync = async (id: ApiRecordKey): Promise<boolean> =>
    apiInstance.delete(`/api/users/${id}/${this.relativeUrl}`);
}
